/*
 * Permissions management system, define access level for each of
 * your server apis, and restrict users by giving them access levels
 *
 * Copyright (C) 2020 Intuition Robotics
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as React from "react";
import {BaseComponent, Dialog_Builder, DialogModule, ToastModule, TS_Input, TS_TextArea} from "@intuitionrobotics/thunderstorm/frontend";
import {generateHex} from "@intuitionrobotics/ts-common";
import {BugReportModule, RequestKey_BugReportApi} from "@intuitionrobotics/bug-report/app-frontend/modules/BugReportModule";
import {Platform_Jira, Platform_Slack} from "@intuitionrobotics/bug-report/shared/api";
import {DialogButton_Cancel, DialogButton_Submit} from "./Dialog";
import * as emotion from "emotion";
import {OnRequestListener} from "@intuitionrobotics/thunderstorm";
import {SimpleLoader} from "@components/SimpleLoader";
type Props = {
    component?: React.ReactNode
    children?: React.ReactNode
}
const style: React.CSSProperties = {
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "fixed",
    width: "50px",
    height: "50px",
    bottom: "30px",
    right: "10px",
    backgroundColor: "#5b7bd6",
    color: "white",
    borderRadius: "50%",
    borderColor: 'transparent'
};
// A subtle "report a bug" FAB: a small circle by default that expands on hover
// to reveal its label (discover-on-hover), so it stays out of the way but is
// clearly a button.
const fabClass = emotion.css(`
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1200;
    display: inline-flex;
    align-items: center;
    height: 44px;
    width: 44px;
    padding: 0;
    border: 1px solid #e7e9ee;
    border-radius: 22px;
    background: #ffffff;
    color: #6b7683;
    cursor: pointer;
    overflow: hidden;
    box-shadow: 0 2px 10px rgba(28, 39, 51, 0.14);
    transition: width .2s ease, background .15s, color .15s, border-color .15s;
    & svg { width: 22px; height: 22px; flex: 0 0 auto; margin: 0 11px; fill: currentColor; }
    & .bug-fab-label { white-space: nowrap; font-size: 13px; font-weight: 700; opacity: 0; transition: opacity .12s; }
    &:hover { width: 158px; background: #fff2e2; color: #b35c00; border-color: #fb7d02; }
    &:hover .bug-fab-label { opacity: 1; }
`)

type State = {
    error?: Error,
    errorInfo?: React.ErrorInfo
    description?: string
    subject?: string

    loading: boolean
}

export class BugReport
    extends BaseComponent<Props, State> implements OnRequestListener {
    __onRequestCompleted(key: string, success: boolean, requestData: string | undefined): void {
        switch (key) {
            case RequestKey_BugReportApi:
                this.setState({loading: false})
        }
    }

    constructor(props: Props) {
        super(props);
        this.state = {
            loading: false
        }
    }

    componentDidMount() {

    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        BugReportModule.sendBugReport("Automatic submission", "these logs were triggered by a UI failure", [Platform_Slack]);
        this.setState({
            error: error,
            errorInfo: errorInfo
        });
    }

    showAppConfirmationDialogExample = () => {
        const onSubmit = () => {
            if (!this.state.subject)
                return ToastModule.toastError('Please add a title to the bug');
            BugReportModule.sendBugReport(this.state.subject, this.state.description || 'Description not provided', [Platform_Jira]);
            this.setState({subject: undefined, description: undefined, loading: true});
            DialogModule.close();
        };

        const content =
            <div className={'ll_v_c'}>
                <TS_Input
                    focus={true}
                    id={"bug-report-subject"}
                    type={"text"}
                    value={this.state.subject || ''}
                    placeholder={"Give a title for context"}
                    name={generateHex(8)}
                    onChange={(subject: string) => this.setState({subject})}
                    style={{
                        width: "100%",
                        border: "1px solid grey",
                        borderRadius: '5px',
                        padding: 5
                    }}
                />
                <TS_TextArea
                    id={"bug-report-description"}
                    type="text"
                    style={{
                        height: "130px",
                        margin: "8px",
                        width: "100%",
                        outline: "none",
                        border: "1px solid grey"
                    }}
                    value={this.state.description || ''}
                    placeholder={"(Optional) Please give as much info as possible"}
                    onChange={(description: string) => this.setState({description})}/>
            </div>;


        new Dialog_Builder(content)
            .setTitle(<div className={'ll_v_c'} style={{padding: 5}}>Bug Report</div>)
            .addButton(DialogButton_Cancel(() => {
                this.setState({description: undefined, subject: undefined})
                DialogModule.close();
            }))
            .addButton(DialogButton_Submit(() => onSubmit(), 'Submit'))
            .setOverlayColor("rgba(102, 255, 255, 0.4)")
            .setStyle({
                minWidth: "400px",
                padding: "10px"
            })
            .show();
    }

    render() {
        if (this.state.errorInfo) {
            return (
                <div>
                    <h2>Something went wrong.</h2>
                    <details style={{whiteSpace: 'pre-wrap'}}>
                        {this.state.error && this.state.error.toString()}
                        <br/>
                        {this.state.errorInfo.componentStack}
                    </details>
                    <button style={style} onClick={() => window.location.reload()}>reload!</button>
                </div>
            );
        }

        return (
            <>
                {this.props.children}
                <button
                    className={fabClass}
                    onClick={this.showAppConfirmationDialogExample}
                    title={"Report a bug"}
                    aria-label={"Report a bug"}
                >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M20 8h-2.81c-.45-.78-1.07-1.45-1.82-1.96L17 4.41 15.59 3l-2.17 2.17C12.96 5.06 12.49 5 12 5c-.49 0-.96.06-1.41.17L8.41 3 7 4.41l1.62 1.63C7.88 6.55 7.26 7.22 6.81 8H4v2h2.09c-.05.33-.09.66-.09 1v1H4v2h2.09c0 .34.04.67.09 1H4v2h2.81c1.04 1.79 2.97 3 5.19 3s4.15-1.21 5.19-3H20v-2h-2.09c.05-.33.09-.66.09-1v-1h2v-2h-2v-1c0-.34-.04-.67-.09-1H20V8zm-6 8h-4v-2h4v2zm0-4h-4v-2h4v2z"/>
                    </svg>
                    <span className="bug-fab-label">Report a bug</span>
                </button>
                {this.renderLoader()}
            </>
        );
    }

    private renderLoader = () => {
        if (!this.state.loading)
            return;

        return <SimpleLoader overlay={true} message={"Sending..."}/>
    };
}
